import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { CheckoutStepType, DeliveryModePreferences, CheckoutEvent, CheckoutSupportedDeliveryModesQueryResetEvent, CheckoutQueryResetEvent, CHECKOUT_BASE_CMS_COMPONENTS, CHECKOUT_FEATURE, CHECKOUT_CORE_FEATURE } from '@spartacus/checkout/base/root';
import * as i1 from '@spartacus/core';
import { LanguageSetEvent, CurrencySetEvent, LogoutEvent, LoginEvent, provideDefaultConfig, provideDefaultConfigFactory, facadeFactory } from '@spartacus/core';
import { Subscription, merge } from 'rxjs';

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
CheckoutStepType['PAYMENT_TYPE'] = 'paymentType';

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const defaultB2BCheckoutConfig = {
  checkout: {
    steps: [{
      id: 'paymentType',
      name: 'checkoutB2B.progress.methodOfPayment',
      routeName: 'checkoutPaymentType',
      type: [CheckoutStepType.PAYMENT_TYPE]
    }, {
      id: 'deliveryAddress',
      name: 'checkoutProgress.deliveryAddress',
      routeName: 'checkoutDeliveryAddress',
      type: [CheckoutStepType.DELIVERY_ADDRESS]
    }, {
      id: 'deliveryMode',
      name: 'checkoutProgress.deliveryMode',
      routeName: 'checkoutDeliveryMode',
      type: [CheckoutStepType.DELIVERY_MODE]
    }, {
      id: 'paymentDetails',
      name: 'checkoutProgress.paymentDetails',
      routeName: 'checkoutPaymentDetails',
      type: [CheckoutStepType.PAYMENT_DETAILS]
    }, {
      id: 'reviewOrder',
      name: 'checkoutProgress.reviewOrder',
      routeName: 'checkoutReviewOrder',
      type: [CheckoutStepType.REVIEW_ORDER]
    }],
    express: false,
    defaultDeliveryMode: [DeliveryModePreferences.FREE],
    guest: false
  }
};

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const defaultCheckoutB2BRoutingConfig = {
  routing: {
    routes: {
      checkoutPaymentType: {
        paths: ['checkout/payment-type']
      }
    }
  }
};

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * An abstract event for all the cost center related events.
 */
class CheckoutCostCenterEvent extends CheckoutEvent {}
/**
 * Fired when the cost center has been set.
 */
let CheckoutCostCenterSetEvent = /*#__PURE__*/(() => {
  class CheckoutCostCenterSetEvent extends CheckoutCostCenterEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutCostCenterSetEvent';
    }
  }
  return CheckoutCostCenterSetEvent;
})();
/**
 * An abstract event for all the payment type related events.
 */
class CheckoutPaymentTypeEvent extends CheckoutEvent {}
/**
 * Emit this event to force payment types reload
 */
let CheckoutPaymentTypesQueryReloadEvent = /*#__PURE__*/(() => {
  class CheckoutPaymentTypesQueryReloadEvent extends CheckoutPaymentTypeEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutPaymentTypesQueryReloadEvent';
    }
  }
  return CheckoutPaymentTypesQueryReloadEvent;
})();
/**
 * Emit this event to force payment types reset
 */
let CheckoutPaymentTypesQueryResetEvent = /*#__PURE__*/(() => {
  class CheckoutPaymentTypesQueryResetEvent extends CheckoutPaymentTypeEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutPaymentTypesQueryResetEvent';
    }
  }
  return CheckoutPaymentTypesQueryResetEvent;
})();
/**
 * Fired when the payment type has been set
 */
let CheckoutPaymentTypeSetEvent = /*#__PURE__*/(() => {
  class CheckoutPaymentTypeSetEvent extends CheckoutPaymentTypeEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutPaymentTypeSetEvent';
    }
  }
  return CheckoutPaymentTypeSetEvent;
})();
/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutCostCenterEventListener = /*#__PURE__*/(() => {
  class CheckoutCostCenterEventListener {
    constructor(eventService) {
      this.eventService = eventService;
      this.subscriptions = new Subscription();
      this.onCostCenterSet();
    }
    onCostCenterSet() {
      this.subscriptions.add(this.eventService.get(CheckoutCostCenterSetEvent).subscribe(({
        cartId,
        userId
      }) => {
        this.eventService.dispatch({
          cartId,
          userId
        }, CheckoutSupportedDeliveryModesQueryResetEvent);
        this.eventService.dispatch({}, CheckoutQueryResetEvent);
      }));
    }
    ngOnDestroy() {
      this.subscriptions.unsubscribe();
    }
    static {
      this.ɵfac = function CheckoutCostCenterEventListener_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutCostCenterEventListener)(i0.ɵɵinject(i1.EventService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutCostCenterEventListener,
        factory: CheckoutCostCenterEventListener.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CheckoutCostCenterEventListener;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutPaymentTypeEventListener = /*#__PURE__*/(() => {
  class CheckoutPaymentTypeEventListener {
    constructor(eventService) {
      this.eventService = eventService;
      this.subscriptions = new Subscription();
      this.onPaymentTypeSet();
      this.onGetPaymentTypesQueryReload();
      this.onGetPaymentTypesQueryReset();
    }
    onPaymentTypeSet() {
      this.subscriptions.add(this.eventService.get(CheckoutPaymentTypeSetEvent).subscribe(({
        userId,
        cartId
      }) => {
        this.eventService.dispatch({
          userId,
          cartId
        }, CheckoutSupportedDeliveryModesQueryResetEvent);
        this.eventService.dispatch({}, CheckoutQueryResetEvent);
      }));
    }
    onGetPaymentTypesQueryReload() {
      this.subscriptions.add(merge(this.eventService.get(LanguageSetEvent), this.eventService.get(CurrencySetEvent)).subscribe(() => {
        this.eventService.dispatch({}, CheckoutPaymentTypesQueryReloadEvent);
      }));
    }
    onGetPaymentTypesQueryReset() {
      this.subscriptions.add(merge(this.eventService.get(LogoutEvent), this.eventService.get(LoginEvent)).subscribe(() => {
        this.eventService.dispatch({}, CheckoutPaymentTypesQueryResetEvent);
      }));
    }
    ngOnDestroy() {
      this.subscriptions.unsubscribe();
    }
    static {
      this.ɵfac = function CheckoutPaymentTypeEventListener_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutPaymentTypeEventListener)(i0.ɵɵinject(i1.EventService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutPaymentTypeEventListener,
        factory: CheckoutPaymentTypeEventListener.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CheckoutPaymentTypeEventListener;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutB2BEventModule = /*#__PURE__*/(() => {
  class CheckoutB2BEventModule {
    constructor(_checkoutCostCenterEventListener, _checkoutPaymentTypeEventListener) {
      // Intentional empty constructor
    }
    static {
      this.ɵfac = function CheckoutB2BEventModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutB2BEventModule)(i0.ɵɵinject(CheckoutCostCenterEventListener), i0.ɵɵinject(CheckoutPaymentTypeEventListener));
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: CheckoutB2BEventModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return CheckoutB2BEventModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const CHECKOUT_B2B_CMS_COMPONENTS = [
/**
 *  TODO:#9574 - we should be able to remove the spread of `CHECKOUT_BASE_CMS_COMPONENTS`.
 * Re-test the B2B checkout flow after doing it.
 */
...CHECKOUT_BASE_CMS_COMPONENTS, 'CheckoutCostCenterComponent', 'CheckoutPaymentType'];
function defaultCheckoutComponentsConfig() {
  const config = {
    featureModules: {
      [CHECKOUT_FEATURE]: {
        cmsComponents: CHECKOUT_B2B_CMS_COMPONENTS
      }
    }
  };
  return config;
}
let CheckoutB2BRootModule = /*#__PURE__*/(() => {
  class CheckoutB2BRootModule {
    static {
      this.ɵfac = function CheckoutB2BRootModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutB2BRootModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: CheckoutB2BRootModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [provideDefaultConfig(defaultB2BCheckoutConfig), provideDefaultConfig(defaultCheckoutB2BRoutingConfig), provideDefaultConfigFactory(defaultCheckoutComponentsConfig)],
        imports: [CheckoutB2BEventModule]
      });
    }
  }
  return CheckoutB2BRootModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutCostCenterFacade = /*#__PURE__*/(() => {
  class CheckoutCostCenterFacade {
    static {
      this.ɵfac = function CheckoutCostCenterFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutCostCenterFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutCostCenterFacade,
        factory: () => (() => facadeFactory({
          facade: CheckoutCostCenterFacade,
          feature: CHECKOUT_CORE_FEATURE,
          methods: ['setCostCenter', 'getCostCenterState']
        }))(),
        providedIn: 'root'
      });
    }
  }
  return CheckoutCostCenterFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutPaymentTypeFacade = /*#__PURE__*/(() => {
  class CheckoutPaymentTypeFacade {
    static {
      this.ɵfac = function CheckoutPaymentTypeFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutPaymentTypeFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutPaymentTypeFacade,
        factory: () => (() => facadeFactory({
          facade: CheckoutPaymentTypeFacade,
          feature: CHECKOUT_CORE_FEATURE,
          methods: ['getPaymentTypes', 'getPaymentTypesState', 'setPaymentType', 'getSelectedPaymentTypeState', 'isAccountPayment', 'getPurchaseOrderNumberState']
        }))(),
        providedIn: 'root'
      });
    }
  }
  return CheckoutPaymentTypeFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
var B2BPaymentTypeEnum = /*#__PURE__*/function (B2BPaymentTypeEnum) {
  B2BPaymentTypeEnum["ACCOUNT_PAYMENT"] = "ACCOUNT";
  B2BPaymentTypeEnum["CARD_PAYMENT"] = "CARD";
  return B2BPaymentTypeEnum;
}(B2BPaymentTypeEnum || {});
/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * Generated bundle index. Do not edit.
 */

export { B2BPaymentTypeEnum, CHECKOUT_B2B_CMS_COMPONENTS, CheckoutB2BEventModule, CheckoutB2BRootModule, CheckoutCostCenterEvent, CheckoutCostCenterEventListener, CheckoutCostCenterFacade, CheckoutCostCenterSetEvent, CheckoutPaymentTypeEvent, CheckoutPaymentTypeEventListener, CheckoutPaymentTypeFacade, CheckoutPaymentTypeSetEvent, CheckoutPaymentTypesQueryReloadEvent, CheckoutPaymentTypesQueryResetEvent, defaultB2BCheckoutConfig, defaultCheckoutB2BRoutingConfig, defaultCheckoutComponentsConfig };
